import { clamp } from './time.js';
export function applyEasing(t, easing) {
    const x = clamp(t, 0, 1);
    switch (easing) {
        case 'linear':
            return x;
        case 'ease-in':
            return x * x;
        case 'ease-out':
            return 1 - (1 - x) * (1 - x);
        case 'ease-in-out':
            return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
        case 'hold':
            return 0;
        default:
            return x;
    }
}
/**
 * A MESMA curva, escrita como expressão do FFmpeg.
 *
 * Mora aqui, colada em `applyEasing`, e não no compilador de render — de propósito.
 * São duas implementações da mesma matemática, e a única defesa contra elas divergirem
 * é ficarem uma embaixo da outra, com um teste que confere as duas. Enquanto esta
 * função não existia, TODO keyframe saía linear no arquivo exportado: o preview
 * desacelerava no fim de um zoom e o MP4 não.
 *
 * `progress` é uma expressão que vale 0..1. Ela é INTERPOLADA várias vezes em vez de
 * guardada com `st()/ld()`: o FFmpeg não garante avaliação preguiçosa dos ramos de
 * `if()`, e um registrador escrito dentro de um ramo que não deveria rodar produziria
 * um valor errado difícil de rastrear. Texto repetido é barato; isso não seria.
 */
export function easingExpression(easing, progress) {
    const p = `(${progress})`;
    switch (easing) {
        case 'linear':
            return p;
        case 'ease-in':
            return `(${p}*${p})`;
        case 'ease-out':
            return `(1-(1-${p})*(1-${p}))`;
        case 'ease-in-out':
            return `(if(lt(${p},0.5),2*${p}*${p},1-pow(-2*${p}+2,2)/2))`;
        case 'hold':
            return '0';
        default:
            return p;
    }
}
/** Valor estático de uma propriedade, quando não há keyframes. */
export function staticValue(item, property) {
    switch (property) {
        case 'transform.x':
            return item.transform.x;
        case 'transform.y':
            return item.transform.y;
        case 'transform.scale':
            return item.transform.scale;
        case 'transform.rotation':
            return item.transform.rotation;
        case 'transform.opacity':
            return item.transform.opacity;
        case 'crop.top':
            return item.crop.top;
        case 'crop.right':
            return item.crop.right;
        case 'crop.bottom':
            return item.crop.bottom;
        case 'crop.left':
            return item.crop.left;
        case 'volume':
            return item.volume;
        default:
            return 0;
    }
}
export function keyframesFor(item, property) {
    return item.keyframes
        .filter((k) => k.property === property)
        .sort((a, b) => a.time - b.time);
}
/**
 * Valor de uma propriedade num tempo LOCAL ao item (segundos desde `item.start`).
 *
 * Sem keyframes → valor estático. Antes do primeiro / depois do último → clamp.
 * `hold` no keyframe da esquerda congela até o próximo.
 */
export function evaluateProperty(item, property, localTime) {
    const keys = keyframesFor(item, property);
    if (keys.length === 0)
        return staticValue(item, property);
    if (keys.length === 1)
        return keys[0].value;
    const first = keys[0];
    const last = keys[keys.length - 1];
    if (localTime <= first.time)
        return first.value;
    if (localTime >= last.time)
        return last.value;
    for (let i = 0; i < keys.length - 1; i++) {
        const a = keys[i];
        const b = keys[i + 1];
        if (localTime >= a.time && localTime <= b.time) {
            const span = b.time - a.time;
            if (span <= 0)
                return b.value;
            const t = applyEasing((localTime - a.time) / span, a.easing);
            return a.value + (b.value - a.value) * t;
        }
    }
    return last.value;
}
export function evaluateItem(item, localTime) {
    return {
        x: evaluateProperty(item, 'transform.x', localTime),
        y: evaluateProperty(item, 'transform.y', localTime),
        scale: evaluateProperty(item, 'transform.scale', localTime),
        rotation: evaluateProperty(item, 'transform.rotation', localTime),
        opacity: evaluateProperty(item, 'transform.opacity', localTime),
        crop: {
            top: evaluateProperty(item, 'crop.top', localTime),
            right: evaluateProperty(item, 'crop.right', localTime),
            bottom: evaluateProperty(item, 'crop.bottom', localTime),
            left: evaluateProperty(item, 'crop.left', localTime),
        },
        volume: evaluateProperty(item, 'volume', localTime),
    };
}
/** Ganho de fade in/out num tempo local, multiplicado sobre o volume. */
export function fadeGain(item, localTime) {
    let gain = 1;
    if (item.fadeIn > 0 && localTime < item.fadeIn) {
        gain *= clamp(localTime / item.fadeIn, 0, 1);
    }
    const fadeOutStart = item.duration - item.fadeOut;
    if (item.fadeOut > 0 && localTime > fadeOutStart) {
        gain *= clamp((item.duration - localTime) / item.fadeOut, 0, 1);
    }
    return gain;
}
export function hasKeyframes(item, property) {
    if (!property)
        return item.keyframes.length > 0;
    return item.keyframes.some((k) => k.property === property);
}
/**
 * Onde e de que tamanho um item visual aparece.
 *
 * Existe para que preview e render não decidam isso cada um por si — a paridade entre o
 * que o usuário vê e o que ele baixa depende de uma única resposta para esta pergunta.
 *
 * A regra difere por tipo, e a diferença é deliberada:
 *
 *   • VÍDEO encaixa no frame, para cima ou para baixo. Um clip 4K numa sequência 1080p
 *     aparece inteiro; um 720p preenche. É o que qualquer editor faz com material.
 *
 *   • IMAGEM entra no tamanho nativo, reduzida só se não couber. Um selo de 600×200
 *     continua com 600×200 — ampliá-lo até a largura do frame transformaria todo
 *     elemento gráfico da biblioteca num cartaz borrado.
 *
 * O `scale` do item multiplica o resultado nos dois casos, e (x, y) deslocam o centro.
 */
export function layoutVisual(item, source, frame, props) {
    const cropX = props.crop.left * source.width;
    const cropY = props.crop.top * source.height;
    const cropW = Math.max(1, source.width * (1 - props.crop.left - props.crop.right));
    const cropH = Math.max(1, source.height * (1 - props.crop.top - props.crop.bottom));
    const contain = Math.min(frame.width / cropW, frame.height / cropH);
    const fit = item.kind === 'image' ? Math.min(1, contain) : contain;
    return {
        width: cropW * fit * props.scale,
        height: cropH * fit * props.scale,
        centerX: frame.width / 2 + props.x,
        centerY: frame.height / 2 + props.y,
        fit,
        crop: { x: cropX, y: cropY, width: cropW, height: cropH },
    };
}
/**
 * Curva de opacidade reduzida a fades.
 *
 * O FFmpeg não aceita expressão no canal alfa sem `geq`, que avalia por pixel e por
 * frame — a 1080p isso custa mais que o encode inteiro. O `fade` resolve exatamente o
 * que o usuário realmente pede (entrar e sair), e é o que este conversor extrai.
 *
 * Uma curva que não seja entrada e/ou saída volta como `exact: false`, e quem chama
 * reporta o item como não compilado em vez de renderizar algo diferente do preview.
 * Silenciar essa diferença seria o pior dos mundos.
 */
export function opacityAsFades(item) {
    const keys = keyframesFor(item, 'transform.opacity');
    const base = { fadeIn: item.fadeIn > 0 && isVisual(item) ? item.fadeIn : 0, fadeOut: item.fadeOut > 0 && isVisual(item) ? item.fadeOut : 0 };
    if (keys.length === 0)
        return { ...base, exact: true };
    const sorted = [...keys].sort((a, b) => a.time - b.time);
    const isZero = (v) => v <= 0.001;
    const isOne = (v) => v >= 0.999;
    let fadeIn = base.fadeIn;
    let fadeOut = base.fadeOut;
    let rest = sorted;
    if (sorted.length >= 2 && isZero(sorted[0].value) && isOne(sorted[1].value) && sorted[0].time <= 0.001) {
        fadeIn = Math.max(fadeIn, sorted[1].time);
        rest = rest.slice(2);
    }
    const n = rest.length;
    if (n >= 2 && isOne(rest[n - 2].value) && isZero(rest[n - 1].value)) {
        const endsAtItemEnd = Math.abs(rest[n - 1].time - item.duration) <= 0.05;
        if (endsAtItemEnd) {
            fadeOut = Math.max(fadeOut, rest[n - 1].time - rest[n - 2].time);
            rest = rest.slice(0, n - 2);
        }
    }
    // Qualquer keyframe que sobre descreve algo que o fade não representa.
    return { fadeIn, fadeOut, exact: rest.every((k) => isOne(k.value)) };
}
function isVisual(item) {
    return item.kind === 'video' || item.kind === 'image' || item.kind === 'shape';
}
//# sourceMappingURL=keyframes.js.map